import { Component, OnInit, inject, input, output } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Observable } from 'rxjs';

import { AuthService, BaseEditFormComponent, IdDescription } from 'tms-lib';
import {
  AddressService,
  CountryReferenceService,
  CustomerSubTypeReferenceService,
  CustomerTypeReferenceService,
  SalesDiscountReferenceService,
  SalesRegionReferenceService,
  TmsFormlyService,
  WarehouseReferenceService,
} from 'tms-web-shared';

@Component({
  selector: 'app-address-details',
  templateUrl: './address-details.component.html',
  styleUrl: './address-details.component.scss',
  imports: [BaseEditFormComponent],
})
export class AddressDetailsComponent implements OnInit {
  protected readonly addressService = inject(AddressService);
  private readonly countryReferenceService = inject(CountryReferenceService);
  private readonly salesRegionReferenceService = inject(SalesRegionReferenceService);
  private readonly salesDiscountReferenceService = inject(SalesDiscountReferenceService);
  private readonly customerTypeReferenceService = inject(CustomerTypeReferenceService);
  private readonly customerSubTypeReferenceService = inject(CustomerSubTypeReferenceService);
  private readonly warehouseReferenceService = inject(WarehouseReferenceService);
  private readonly authService = inject(AuthService);
  private readonly formlyService = inject(TmsFormlyService);

  public readonly closeOnSave = input(true);
  public readonly id = input<string>();
  public readonly saved = output<any>();

  protected fields: FormlyFieldConfig[];

  private isCustomer = false;

  private countries$: Observable<IdDescription[]>;
  private salesRegion$: Observable<IdDescription[]>;
  private salesDiscount$: Observable<IdDescription[]>;
  private customerType$: Observable<IdDescription[]>;
  private customerSubType$: Observable<IdDescription[]>;
  private warehouses$: Observable<IdDescription[]>;

  ngOnInit() {
    this.getReferenceData();
    this.getFields();
    this.isCustomer = this.authService.isCustomer;
  }

  private getReferenceData() {
    this.countries$ = this.countryReferenceService.get();
    this.salesRegion$ = this.salesRegionReferenceService.get();
    this.salesDiscount$ = this.salesDiscountReferenceService.get();
    this.customerType$ = this.customerTypeReferenceService.get();
    this.customerSubType$ = this.customerSubTypeReferenceService.get();
    this.warehouses$ = this.warehouseReferenceService.get();
  }

  private getFields() {
    this.fields = this.formlyService.twoColumnGrid(
      [
        this.formlyService.text('addressee', $localize`Addressee`, { maxLength: 60 }),
        this.formlyService.text('name', $localize`Name`, { maxLength: 60 }),
        this.formlyService.addressLookup('addressLookup', this.countries$),
        this.formlyService.text('line1', $localize`Address Line 1`, { required: true, maxLength: 80 }),
        this.formlyService.text('line2', $localize`Address Line 2`, { maxLength: 80 }),
        this.formlyService.text('line3', $localize`Address Line 3`, { maxLength: 80 }),
        this.formlyService.text('line4', $localize`Address Line 4`, { maxLength: 80 }),
        this.formlyService.text('townCity', $localize`Town/City`, { maxLength: 80 }),
        this.formlyService.text('region', $localize`Region`, { maxLength: 80 }),
        this.formlyService.text('postCode', $localize`Postcode`, { maxLength: 80 }),
        this.formlyService.select('countryId', $localize`Country`, this.countries$, { required: true }),
        this.formlyService.text('phone', $localize`Telephone`, { maxLength: 30 }),
        this.formlyService.text('email', $localize`Email`, { maxLength: 255 }),
        this.formlyService.checkbox('webAddress', $localize`Web Address`, undefined, () => this.isCustomer),
        this.formlyService.text('vatRegNo', $localize`Vat Registration Number`, {
          maxLength: 50,
        }),
        this.formlyService.checkbox('obsolete', $localize`Obsolete`, undefined, () => this.isCustomer),
      ],
      [
        this.formlyService.select(
          'salesRegionId',
          $localize`Sales Region`,
          this.salesRegion$,
          undefined,
          () => this.isCustomer,
        ),
        this.formlyService.select(
          'salesDiscountId',
          $localize`Sales Discount`,
          this.salesDiscount$,
          undefined,
          () => this.isCustomer,
        ),
        this.formlyService.select(
          'customerTypeId',
          $localize`Customer Type`,
          this.customerType$,
          undefined,
          () => this.isCustomer,
        ),
        this.formlyService.select(
          'customerSubTypeId',
          $localize`Customer Sub Type`,
          this.customerSubType$,
          undefined,
          () => this.isCustomer,
        ),
        this.formlyService.text('eoriNumber', $localize`EORI Number`, undefined, () => this.isCustomer),
        this.formlyService.textarea('salesDeclaration', $localize`Sales Declaration`),
        // Where goods delivered here LAND. Set on the company's own delivery points (Domus,
        // the ZigZag warehouse) so a purchase order delivered here takes it as its Warehouse
        // and the replenishment rule counts the PO as on order there. Leave blank on a
        // customer's address. Hidden from customer-portal users like the other trade fields.
        this.formlyService.select(
          'warehouseId',
          $localize`Warehouse Goods Land In`,
          this.warehouses$,
          undefined,
          () => this.isCustomer,
        ),
      ],
    );
  }
}
